import React, { useEffect, useRef, useState } from 'react';
import '../styles/TicketForm.css';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Edit3, MessageSquare, User, Tag, Home, HardDrive, Phone, Calendar, ShieldAlert, Paperclip, Lock } from 'lucide-react';

const TicketModal = ({ isFormOpen, setIsFormOpen, selectedTicket, formData, setFormData, handleSubmit, user }) => {
  const fileInputRef = useRef(null);
  // NEW: State for the staff member to explain their action
  const [actionNote, setActionNote] = useState("");
  

  useEffect(() => {
    if (isFormOpen && !selectedTicket) {
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({ 
        ...prev, 
        date: today,
        ticket_owner: user?.name || "Unknown",
        status: prev.status || "Open",
        priority: prev.priority || "Medium",
        category: prev.category || "General"
      }));
      setActionNote(""); // Reset note
    }
  }, [isFormOpen, selectedTicket, user, setFormData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, attachment: file });
    }
  };

  // NEW: Wrapper to pass the note to your existing handleSubmit
  const onFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e, actionNote); 
    setActionNote(""); // Clear after submit
  };

  return (
    <AnimatePresence>
      {isFormOpen && (
        <div className="modal-overlay">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            exit={{ opacity: 0, scale: 0.95 }} 
            className="modal-card wide-modal" 
          >
            <div className="modal-header">
              <div className="modal-title-combined">
                <div className="modal-icon-circle">
                  <Edit3 size={20} color="#6366f1" />
                </div>
                <div>
                  <h3>{selectedTicket ? 'Staff: Update Ticket Activity' : 'Create New Ticket'}</h3>
                  <p>Current User: <strong>{user?.name}</strong></p>
                </div>
              </div>
              <button className="close-modal" onClick={() => setIsFormOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={onFormSubmit} className="modern-form scrollable-form" encType="multipart/form-data">
              
              {/* NEW: Warning for Staff members editing a ticket */}
              {selectedTicket && (
                <div style={{ backgroundColor: '#eef2ff', padding: '12px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #c7d2fe' }}>
                   <Lock size={16} color="#6366f1" />
                   <span style={{ fontSize: '13px', color: '#4338ca', fontWeight: '500' }}>Property details are locked to maintain record integrity. Please provide an update below.</span>
                </div>
              )}

              {/* SECTION 1: CORE DETAILS - Now conditionally disabled if editing */}
              <div className={`form-section ${selectedTicket ? 'locked-section' : ''}`} style={selectedTicket ? {opacity: 0.6, pointerEvents: 'none'} : {}}>
                <h4 className="section-title">Property & Contact Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label><User size={14} /> Contact Name</label>
                    <input type="text" placeholder="Enter contact name" value={formData.contact_name || ""} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} required={!selectedTicket} />
                  </div>
                  <div className="form-group">
                    <label><Home size={14} /> Property Name</label>
                    <input type="text" placeholder="Property name" value={formData.property_name || ""} onChange={(e) => setFormData({ ...formData, property_name: e.target.value })} required={!selectedTicket} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label><User size={14} /> Property Tenant (Optional)</label>
                    <input type="text" placeholder="Tenant name" value={formData.property_tenant || ""} onChange={(e) => setFormData({ ...formData, property_tenant: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label><Phone size={14} /> Property Phone</label>
                    <input type="tel" placeholder="Phone number" value={formData.property_phone || ""} onChange={(e) => setFormData({ ...formData, property_phone: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* NEW SECTION: STAFF ACTION LOG (Only shows when editing) */}
              {selectedTicket && (
                <div className="form-section" style={{ border: '2px solid #6366f1', backgroundColor: '#f5f3ff' }}>
                  <h4 className="section-title" style={{ color: '#6366f1' }}>Internal Staff Note (Required)</h4>
                  <div className="form-group">
                    <label><MessageSquare size={14} /> What action was taken?</label>
                    <textarea 
                      placeholder="e.g. 'Replaced VOIP handset' or 'Called tenant to confirm fix'..." 
                      rows="4" 
                      value={actionNote} 
                      onChange={(e) => setActionNote(e.target.value)} 
                      required 
                    />
                  </div>
                </div>
              )}

              <div className="form-section">
                <h4 className="section-title">System & Documentation</h4>
                <div className="form-row">
                  {/* Property Type (Existing) */}
                  <div className="form-group" style={selectedTicket ? {opacity: 0.6, pointerEvents: 'none'} : {}}>
                    <label>Property Type</label>
                    <select value={formData.property_type || ""} onChange={(e) => setFormData({ ...formData, property_type: e.target.value })} required={!selectedTicket}>
                      <option value="" disabled>Select Type</option>
                      <option value="VOIP">VOIP</option>
                      <option value="Guest">Guest</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>

                  {/* NEW: Server Type Selection */}
                  <div className="form-group" style={selectedTicket ? {opacity: 0.6, pointerEvents: 'none'} : {}}>
                    <label><HardDrive size={14} /> Server Type</label>
                    <select 
                      value={formData.server_type || ""} 
                      onChange={(e) => setFormData({ ...formData, server_type: e.target.value })}
                      required={!selectedTicket}
                    >
                      <option value="" disabled>Select Server</option>
                      <option value="Core Cloud">Core Cloud</option>
                      <option value="Hudo Cloud">Hudo Cloud</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label><Paperclip size={14} /> {selectedTicket ? "Add New Attachment" : "Attachment (Optional)"}</label>
                    <div className="file-input-wrapper">
                      <input type="file" id="file-upload" onChange={handleFileChange} style={{ display: 'none' }} ref={fileInputRef} />
                      <button type="button" className="file-custom-btn" onClick={() => fileInputRef.current.click()}>
                        {formData.attachment ? formData.attachment.name : "Choose File..."}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsFormOpen(false)}>Cancel</button>
                {selectedTicket?.status === 'Closed' ? (
                  <button type="button" className="submit-btn disabled" style={{ backgroundColor: '#94a3b8', cursor: 'not-allowed' }} disabled>
                    <Lock size={18} style={{ marginRight: '8px' }} />
                    Ticket Closed (No Edits)
                  </button>
                ) : (
                  <button type="submit" className="submit-btn">
                    <Send size={18} style={{ marginRight: '8px' }} />
                    {selectedTicket ? 'Save Staff Update' : 'Create Ticket'}
                  </button>
                )}
              </div> */}

              {/* SECTION 3: ISSUE DETAILS - Locked if editing */}
              <div className="form-section" style={selectedTicket ? {opacity: 0.6, pointerEvents: 'none'} : {}}>
                <h4 className="section-title">Issue Details</h4>
                <div className="form-group">
                  <label>Subject</label>
                  <input type="text" placeholder="Ticket subject" value={formData.subject || ""} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required={!selectedTicket} />
                </div>
                <div className="form-groupd">
                  <label><MessageSquare size={14} /> Description</label>
                  <textarea placeholder="Describe the issue in detail..." rows="3" value={formData.description || ""} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                </div>
              </div>

              {/* SECTION 4: ADDITIONAL INFO (Always Editable for Staff) */}
              <div className="form-section info-highlight">
                <h4 className="section-title">Ticket Status Management</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label><Calendar size={14} /> Date</label>
                    <input type="date" value={formData.date || ""} readOnly className="readonly-input" />
                  </div>
                  <div className="form-group">
                    <label><ShieldAlert size={14} /> Priority</label>
                    <select value={formData.priority || "Medium"} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select value={formData.status || "Open"} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsFormOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">
                  <Send size={18} style={{ marginRight: '8px' }} />
                  {selectedTicket ? 'Save Staff Update' : 'Create Ticket'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TicketModal;